// libs
import { GraphQLResult } from "@aws-amplify/api-graphql";
import { UseQueryResult, useQuery } from "@tanstack/react-query";

// utils
import { measureLatency } from "@helpers/index";
import { formatGraphQLError } from "@helpers/Error";
import { ConfigType } from "@utils/appsync/schema/API";
import { getClient, graphQlQueries } from "@utils/appsync";

// hooks
import { useLogger } from "@hooks/useLogger";
import { useMetrics } from "@hooks/useMetrics";

// constants
import { MetricName } from "@constants/Metrics";
import { ServiceType } from "@constants/Services";

export const useGetFeatureFlags = (userAlias: string, enableFetch: boolean = true): UseQueryResult<object, Error> => {
    const { publishErrorLog } = useLogger();

    const { publishFailureMetric, publishLatencyMetric } = useMetrics();

    return useQuery<object, Error>({
        queryKey: ["featureFlags", userAlias],
        queryFn: async () => {
            const startTime = Date.now();

            const { data, errors }: GraphQLResult<any> = await getClient().graphql({
                query: graphQlQueries.GetConfigurations,
                variables: {
                    userAlias,
                    configType: ConfigType.FEATURE_FLAG,
                },
            });

            // publish the featureFlags service latency metrics
            publishLatencyMetric(MetricName.GET_FEATURE_FLAGS_LATENCY, measureLatency(startTime), ServiceType.FeatureFlagService);

            // in case of featureFlags querry failure, publish the logs and metrics to cloudwatch
            if (errors?.length) {
                // publishing the error logs
                publishErrorLog(errors.map((error) => formatGraphQLError(error)));

                // publish the failure metrics
                publishFailureMetric(MetricName.GET_FEATURE_FLAGS_ERROR_COUNT, ServiceType.FeatureFlagService);
            }

            return data.GetConfigurations?.content?.length ? JSON.parse(data.GetConfigurations.content) : {};
        },
        enabled: !!userAlias && enableFetch,
    });
};
